import React, { useState, useRef, useEffect } from "react";
import {
  PaperAirplaneIcon,
  XMarkIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import { supabase } from "../src/lib/supabaseClient";
// CSS removed in favor of Tailwind

/* ================= TYPES ================= */

interface Message {
  id: string;
  text: string;
  sender: "user" | "aria";
  type?: "text" | "options" | "calendar";
  options?: string[];
  metadata?: any;
  timestamp?: Date;
}

interface AIAssistantProps {
  onNavigate: (page: any, initialStateData?: any) => void;
}

/* ================= COMPONENT ================= */

const AIAssistant: React.FC<AIAssistantProps> = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "init1",
      sender: "aria",
      text: "Hi there, I’m Aria, your Cherry AI agent. I'm here to help you transform your business.",
    },
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  /* ================= AUTO SCROLL ================= */

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  /* ================= ONBOARDING GREETING ================= */

  useEffect(() => {
    const hasGreeted = sessionStorage.getItem("aria_onboarding_greeted");
    if (!hasGreeted) {
      setMessages((prev) => [
        ...prev,
        {
          id: "onboarding-welcome",
          sender: "aria",
          text: "Welcome! I'll help you set up Cherry CRM step by step.\nLet's start with your company details.",
          timestamp: new Date(),
        },
      ]);
      sessionStorage.setItem("aria_onboarding_greeted", "true");
    }
  }, []);

  /* ================= VOICE SYNTHESIS ================= */

  const speak = (text: string) => {
    if (!isVoiceEnabled || !window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    const voices = window.speechSynthesis.getVoices();

    if (!voices.length) {
      window.speechSynthesis.onvoiceschanged = () => speak(text);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice =
      voices.find((v) => v.name.includes("Microsoft Zira")) ||
      voices.find((v) => v.name === "Google US English") ||
      voices.find((v) => v.name === "Samantha") ||
      voices.find((v) => v.default) ||
      null;

    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.sender === "aria" && last.id !== "init1") {
      speak(last.text);
    }
  }, [messages, isVoiceEnabled]);

  /* ================= PROCESS AI RESPONSE ================= */

  const processAriaResponse = (response: any | any[]) => {
    const responses = Array.isArray(response) ? response : [response];

    const ariaMessages = responses.map((msg) => ({
      ...msg,
      sender: "aria" as const,
    }));

    setMessages((prev) => [...prev, ...ariaMessages]);

    responses.forEach((msg) => {
      if (msg.metadata?.action === "NAVIGATE") {
        onNavigate(msg.metadata.page, msg.metadata.state);
      }
    });
  };

  /* ================= SEND MESSAGE ================= */

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    setMessages((prev) => [
      ...prev,
      { id: "typing", sender: "aria", text: "..." },
    ]);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: userMessage.text }),
      });

      if (!res.ok) throw new Error("AI request failed");
      const aiResponse = await res
        .json()
        .then((j) => (j?.data !== undefined ? j.data : j));

      setMessages((prev) => prev.filter((m) => m.id !== "typing"));
      processAriaResponse(aiResponse);
    } catch (error) {
      console.error(error);
      setMessages((prev) => prev.filter((m) => m.id !== "typing"));
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          sender: "aria",
          text: "I'm having trouble connecting right now. Please try again.",
        },
      ]);
    }
  };

  /* ================= UI ================= */

  if (!isOpen) {
    return (
      <div
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-xl cursor-pointer flex items-center justify-center hover:bg-primary-hover transition-colors z-[100]"
        aria-label="Open Aria AI"
      >
        {/* Using a simple sparkle/robot icon or the logo if available. Using sparkle for now as per design system hints */}
        <span className="text-2xl">✨</span>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-[340px] h-[500px] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 z-[100] font-sans">
      {/* Header */}
      <div className="flex justify-between items-center px-4 h-12 bg-primary text-white shrink-0">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <span>✨</span> Aria AI
        </h3>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setIsVoiceEnabled((v) => !v)}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            {isVoiceEnabled ? (
              <SpeakerWaveIcon className="h-4 w-4" />
            ) : (
              <SpeakerXMarkIcon className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Messages Body - FAFAFA Background */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAFA]">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`max-w-[85%] ${
              msg.sender === "user" ? "ml-auto text-right" : "mr-auto"
            }`}
          >
            <div
              className={`px-4 py-2 rounded-lg text-sm leading-relaxed ${
                msg.sender === "user"
                  ? "bg-primary text-white rounded-br-none"
                  : "bg-white text-gray-900 border border-gray-200 rounded-bl-none shadow-sm"
              }`}
            >
              {msg.text}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Footer */}
      <div className="p-3 bg-white border-t border-gray-200 shrink-0">
        <div className="flex gap-2">
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
            className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder="Ask Aria anything..."
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
            className="bg-primary text-white w-9 h-9 flex items-center justify-center rounded-full hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PaperAirplaneIcon className="h-4 w-4 -ml-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
